import React, { useEffect, useMemo, useState } from "react";
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EuroIcon from '@mui/icons-material/Euro';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FlagIcon from '@mui/icons-material/Flag';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import MenuOpenOutlinedIcon from '@mui/icons-material/MenuOpenOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import YardIcon from '@mui/icons-material/Yard';
import { getCategories } from 'services/api/Shop';
import ProductStore from 'services/store/ProductStore';
import "./LeftPanel.scss";

const buildNavigationTree = (categories = []) => {
  const navigationMap = new Map();

  categories.forEach((category) => {
    if (category.type === 'special') {
      navigationMap.set(category.id, {
        id: category.id,
        name: category.name,
        type: category.type,
        level: 1,
        category,
        children: [],
        rootType: category.type,
        ancestorIds: [],
      });
      return;
    }

    const level1 = category.parent?.parent;
    const level2 = category.parent;

    if (!level1?.id) {
      return;
    }

    if (!navigationMap.has(level1.id)) {
      navigationMap.set(level1.id, {
        id: level1.id,
        name: level1.name,
        type: level1.type,
        level: 1,
        category: level1,
        children: [],
        rootType: level1.type,
        ancestorIds: [],
      });
    }

    const rootNode = navigationMap.get(level1.id);

    if (!level2?.id) {
      return;
    }

    let secondLevelNode = rootNode.children.find((child) => child.id === level2.id);

    if (!secondLevelNode) {
      secondLevelNode = {
        id: level2.id,
        name: level2.name,
        type: level2.type,
        level: 2,
        category: level2,
        children: [],
        rootType: rootNode.type,
        ancestorIds: [rootNode.id],
      };
      rootNode.children.push(secondLevelNode);
    }

    secondLevelNode.children.push({
      id: category.id,
      name: category.name,
      type: category.type,
      level: 3,
      category,
      children: [],
      rootType: rootNode.type,
      ancestorIds: [rootNode.id, secondLevelNode.id],
    });
  });

  return Array.from(navigationMap.values());
};

const findAncestorPath = (navigation, targetId) => {
  for (const item of navigation) {
    if (item.id === targetId) {
      return item.ancestorIds;
    }

    if (item.children.length > 0) {
      const secondLevelMatch = item.children.find((child) => child.id === targetId);
      if (secondLevelMatch) {
        return secondLevelMatch.ancestorIds;
      }

      for (const child of item.children) {
        const thirdLevelMatch = child.children.find((grandChild) => grandChild.id === targetId);
        if (thirdLevelMatch) {
          return thirdLevelMatch.ancestorIds;
        }
      }
    }
  }

  return [];
};

const normalizeCategoryName = (value = '') => (
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
);

const getTopLevelIconByName = (categoryName) => {
  const normalizedName = normalizeCategoryName(categoryName);

  if (!normalizedName) {
    return null;
  }

  if (
    normalizedName.includes('promo') ||
    normalizedName.includes('offre') ||
    normalizedName.includes('bon plan')
  ) {
    return LocalOfferOutlinedIcon;
  }

  if (
    normalizedName.includes('prix') ||
    normalizedName.includes('budget') ||
    normalizedName.includes('euro') ||
    normalizedName.includes('econom')
  ) {
    return EuroIcon;
  }

  if (
    normalizedName.includes('favori') ||
    normalizedName.includes('coeur') ||
    normalizedName.includes('amour') ||
    normalizedName.includes('romance') ||
    normalizedName.includes('saint valentin')
  ) {
    return FavoriteIcon;
  }

  if (
    normalizedName.includes('france') ||
    normalizedName.includes('origine') ||
    normalizedName.includes('local') ||
    normalizedName.includes('drapeau')
  ) {
    return FlagIcon;
  }

  if (
    normalizedName.includes('eco') ||
    normalizedName.includes('bio') ||
    normalizedName.includes('nature') ||
    normalizedName.includes('vert') ||
    normalizedName.includes('compost') ||
    normalizedName.includes('responsable')
  ) {
    return YardIcon;
  }

  return null;
};

const LeftPanel = ({ token, loadProducts, showHome, width, webShopId, loadParcel, countRefresh }) => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showLeftBar, setShowLeftBar] = useState(false);
  const [active, setActive] = useState("");
  const [expandedItems, setExpandedItems] = useState([]);
  const isMobile = useMediaQuery('(max-width:767px)');

  const navigation = useMemo(() => buildNavigationTree(categories), [categories]);

  const closeMobileSidebar = () => {
    if (isMobile) {
      setShowLeftBar(false);
    }
  };

  const syncCategoryQuery = (categoryId) => {
    const url = new URL(window.location);

    if (categoryId) {
      url.searchParams.set("category", categoryId);
    } else {
      url.searchParams.delete("category");
    }

    window.history.pushState({}, "", url);
  };

  const expandPath = (itemIds = []) => {
    if (itemIds.length === 0) {
      return;
    }

    setExpandedItems((currentItems) => {
      const nextItems = new Set(currentItems);
      itemIds.forEach((itemId) => nextItems.add(itemId));
      return Array.from(nextItems);
    });
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems((currentItems) => (
      currentItems.includes(itemId)
        ? currentItems.filter((currentItemId) => currentItemId !== itemId)
        : [...currentItems, itemId]
    ));
  };

  const selectProductsCategory = (category, ancestorIds = []) => {
    closeMobileSidebar();
    ProductStore.currentPage = 1;
    syncCategoryQuery(category.id);
    expandPath(ancestorIds);
    setActive(category.id);
    loadProducts(category);
  };

  const selectParcelCategory = (category, ancestorIds = []) => {
    closeMobileSidebar();
    expandPath(ancestorIds);
    setActive(category.id);
    loadParcel(category);
  };

  const handleShowHome = async () => {
    closeMobileSidebar();
    syncCategoryQuery(null);
    await showHome();
    setActive("home");
  };

  useEffect(() => {
    let isMounted = true;

    const loadNavigation = async () => {
      setIsLoading(true);

      try {
        const data = await getCategories(token);

        if (!isMounted) {
          return;
        }

        const nextCategories = data.content || [];
        const nextNavigation = buildNavigationTree(nextCategories);
        const initialCategoryId = new URL(window.location).searchParams.get("category");

        setCategories(nextCategories);

        if (initialCategoryId) {
          const selectedCategory = nextCategories.find((category) => `${category.id}` === `${initialCategoryId}`);

          if (selectedCategory) {
            expandPath(findAncestorPath(nextNavigation, selectedCategory.id));
            setActive(selectedCategory.id);
            loadProducts(selectedCategory);
          } else {
            await handleShowHome();
          }
        } else {
          await handleShowHome();
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadNavigation();

    return () => {
      isMounted = false;
    };
  }, [token, webShopId, countRefresh]);

  useEffect(() => {
    if (!isMobile) {
      setShowLeftBar(false);
      return undefined;
    }

    const openTimer = window.setTimeout(() => {
      setShowLeftBar(true);
    }, 3000);

    return () => {
      window.clearTimeout(openTimer);
    };
  }, [isMobile, token, webShopId, countRefresh]);

  const getItemIcon = (item, topLevelIndex) => {
    const topLevelIcons = [EuroIcon, FavoriteIcon, FlagIcon, YardIcon];

    if (item.level === 1 && typeof topLevelIndex === 'number') {
      const NamedTopLevelIcon = getTopLevelIconByName(item.name);

      if (NamedTopLevelIcon) {
        return <NamedTopLevelIcon fontSize="small" />;
      }

      if (topLevelIndex === 0) {
        return <LocalOfferOutlinedIcon fontSize="small" />;
      }

      const TopLevelIcon = topLevelIcons[topLevelIndex - 1];

      if (TopLevelIcon) {
        return <TopLevelIcon fontSize="small" />;
      }
    }

    if (item.type === 'special') {
      return <LocalOfferOutlinedIcon fontSize="small" />;
    }

    if (item.level === 1 && item.rootType === 'parcel') {
      return <Inventory2OutlinedIcon fontSize="small" />;
    }

    if (item.level === 1) {
      return <CategoryOutlinedIcon fontSize="small" />;
    }

    return <LocalFloristIcon fontSize="small" />;
  };

  const renderNavigationItems = (items, depth = 0) => (
    items.map((item, index) => {
      const hasChildren = item.children.length > 0;
      const isExpanded = expandedItems.includes(item.id);
      const isSelected = active === item.id;

      const handleItemClick = () => {
        if (item.level === 1 && item.type === 'special') {
          selectProductsCategory(item.category, item.ancestorIds);
          return;
        }

        if (item.level === 1) {
          toggleExpanded(item.id);
          setActive((currentActive) => (currentActive === item.id ? "" : item.id));
          return;
        }

        if (item.level === 2) {
          toggleExpanded(item.id);

          if (item.rootType !== 'parcel') {
            selectProductsCategory(item.category, item.ancestorIds);
          } else {
            setActive(item.id);
          }

          return;
        }

        if (item.rootType === 'parcel') {
          selectParcelCategory(item.category, item.ancestorIds);
          return;
        }

        selectProductsCategory(item.category, item.ancestorIds);
      };

      return (
        <React.Fragment key={item.id}>
          <ListItemButton
            selected={isSelected}
            onClick={handleItemClick}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              mx: 1,
              minHeight: item.level === 1 ? 44 : 40,
              pl: 1.5 + (depth * 2),
              pr: 1.5,
              py: 0.25,
              position: 'relative',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: depth === 0 ? 'action.hover' : 'grey.50',
              },
              ...(depth > 0 ? {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 16 + ((depth - 1) * 16),
                  top: 8,
                  bottom: 8,
                  width: 2,
                  borderRadius: 999,
                  backgroundColor: isSelected ? 'primary.main' : 'divider',
                  opacity: 0.9,
                },
              } : {}),
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                boxShadow: (theme) => `0 8px 20px ${theme.palette.primary.main}26`,
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText',
                },
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isSelected ? 'inherit' : 'primary.main',
                minWidth: 34,
                ml: depth > 0 ? 1.5 : 0,
              }}
            >
              {getItemIcon(item, depth === 0 ? index : undefined)}
            </ListItemIcon>
            <ListItemText
              primary={item.name}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: item.level === 1 ? 600 : 500,
                noWrap: true,
                title: item.name,
                color: isSelected ? 'inherit' : item.level === 1 ? 'text.primary' : 'text.secondary',
              }}
            />
            {hasChildren ? (isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />) : null}
          </ListItemButton>

          {hasChildren ? (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List disablePadding>
                {renderNavigationItems(item.children, depth + 1)}
              </List>
            </Collapse>
          ) : null}
        </React.Fragment>
      );
    })
  );

  const navigationContent = (
    <Box className="left-panel-content" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        className="left-panel-header"
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          background: (theme) => `linear-gradient(180deg, ${theme.palette.primary.light}12 0%, ${theme.palette.common.white} 100%)`,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1 }}>
          Catalogue
        </Typography>
        <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
          Fleurs & collections
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {navigation.length} familles disponibles
        </Typography>
      </Box>

      <Box className="left-panel-scroll" sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        <Box sx={{ px: 2, pb: 0.5, pt: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
            Navigation
          </Typography>
        </Box>
        <List disablePadding sx={{ p: 1, pt: 0.5 }}>
          <ListItemButton
          selected={active === 'home'}
          onClick={handleShowHome}
            sx={{
              borderRadius: 2,
              mb: 1,
              minHeight: 44,
              boxShadow: active === 'home' ? (theme) => `0 8px 20px ${theme.palette.primary.main}26` : 'none',
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '& .MuiListItemIcon-root': {
                  color: 'primary.contrastText',
                },
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: active === 'home' ? 'inherit' : 'primary.main', minWidth: 34 }}>
              <HomeOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Accueil"
              primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
            />
          </ListItemButton>
        </List>

        <Divider />

        {isLoading ? (
          <Box className="categories-loading" sx={{ flex: 1 }}>
            <CircularProgress size={38} />
          </Box>
        ) : (
          <List disablePadding sx={{ p: 1, pt: 1.5 }}>
            {renderNavigationItems(navigation)}
          </List>
        )}
      </Box>

      <Box
        className="left-panel-footer"
        sx={{
          px: 2,
          py: 1.25,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: 'grey.50',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Sélectionnez une famille puis une fleur pour afficher les produits.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <div
      className={`left-panel ${showLeftBar ? '' : 'collapsed'} ${isMobile ? 'is-mobile' : ''}`}
      style={isMobile ? undefined : { width }}
    >
      {isMobile ? (
        <>
          <Tooltip title={showLeftBar ? 'Fermer les catégories' : 'Ouvrir les catégories'}>
            <IconButton className="menu-left-mobile" onClick={() => setShowLeftBar(!showLeftBar)} size="small">
              {showLeftBar ? <MenuOpenOutlinedIcon fontSize="small" /> : <MenuOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Drawer
            anchor="left"
            open={showLeftBar}
            onClose={() => setShowLeftBar(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              className: 'left-panel-drawer-paper',
              sx: {
                width: '80vw',
                maxWidth: 360,
              },
            }}
          >
            {navigationContent}
          </Drawer>
        </>
      ) : (
        <Paper
          elevation={0}
          className="left-panel-surface"
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            boxShadow: (theme) => theme.shadows[2],
            height: '100%',
            minHeight: 'calc(100vh - 180px)',
            overflow: 'hidden',
          }}
        >
          {navigationContent}
        </Paper>
      )}
    </div>
  );
};

export default LeftPanel;
